import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiDollarSign, FiTrendingUp, FiRefreshCw, FiHome } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const uses = [
  {
    icon: <FiTrendingUp className="size-6" />,
    title: "Business expansion",
    description: "Fund new locations, markets, or capacity increases.",
  },
  {
    icon: <FiDollarSign className="size-6" />,
    title: "Working capital",
    description: "Cover operational costs, inventory, or seasonal fluctuations.",
  },
  {
    icon: <FiRefreshCw className="size-6" />,
    title: "Debt refinancing",
    description: "Consolidate existing debts or secure better terms.",
  },
  {
    icon: <FiHome className="size-6" />,
    title: "Property purchase",
    description: "Acquire commercial property for your business.",
  },
];

const loanTypes = [
  {
    title: "Secured Business Loan",
    description: "Lower rates by using property or assets as security. Higher borrowing limits available.",
    security: "Property or assets",
    typical: "$50K - $5M+",
    term: "1-30 years",
  },
  {
    title: "Unsecured Business Loan",
    description: "No security required, faster approval. Higher rates but more accessible for many businesses.",
    security: "None required",
    typical: "$5K - $500K",
    term: "1-5 years",
  },
  {
    title: "Line of Credit",
    description: "Flexible access to funds as needed. Only pay interest on what you use. Can redraw repaid amounts.",
    security: "Varies",
    typical: "$10K - $1M+",
    term: "Ongoing",
  },
  {
    title: "Overdraft",
    description: "Linked to your business account. Access funds up to your limit to cover short-term needs.",
    security: "Varies",
    typical: "$5K - $250K",
    term: "Annual review",
  },
];

const requirements = [
  {
    title: "Trading history",
    description: "Most lenders want to see 6-24 months of trading. Some fintech lenders accept newer businesses.",
  },
  {
    title: "Financial statements",
    description: "Recent BAS statements, tax returns, and/or management accounts depending on the lender.",
  },
  {
    title: "Business plan",
    description: "For larger loans, lenders may want to understand your business strategy and use of funds.",
  },
  {
    title: "Security (for secured loans)",
    description: "Property, equipment, or other assets. Personal guarantees are often required.",
  },
];

const process = [
  {
    step: 1,
    title: "Initial discussion",
    description: "We understand your business, funding needs, and timeline.",
  },
  {
    step: 2,
    title: "Document collection",
    description: "We gather the required financials and business information.",
  },
  {
    step: 3,
    title: "Lender matching",
    description: "We identify the best lenders for your situation and submit applications.",
  },
  {
    step: 4,
    title: "Approval & settlement",
    description: "We manage the approval process and arrange settlement.",
  },
];

export function TermLoansPage() {
  return (
    <>
      <SEO
        title="Business Term Loans Brisbane, Ipswich & Springfield"
        description="Business term loans for expansion, working capital or refinancing in Brisbane, Ipswich, Springfield & Logan. Secured & unsecured options from 70+ lenders."
        url="/business-loans/term-loans"
        keywords={[
          "business term loans Brisbane",
          "business loans Ipswich",
          "business loans Springfield",
          "secured business loan",
          "unsecured business loan",
          "business funding Brisbane",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Business Term Loans",
            description:
              "Secured and unsecured business term loans for working capital, expansion, refinancing and acquisitions. Compared from 70+ lenders.",
            url: "/business-loans/term-loans",
            serviceType: "Business Loan",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Business Loans", url: "/business-loans" },
            { name: "Term Loans", url: "/business-loans/term-loans" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/business-loans"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <FiArrowRight className="size-4 mr-2 rotate-180" />
              Business Loans
            </Link>
            <p className="section-label mb-4">Term Loans</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Flexible funding for your business
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Whether you need working capital, expansion funding, or want to
              refinance existing debt, we'll find the right term loan structure
              from our panel of business lenders.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                Discuss Your Needs
                <FiArrowRight className="size-4" />
              </Link>
              <Link
                to={`tel:${broker.phone.replace(/\s/g, "")}`}
                className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
              >
                Call {broker.phone}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Uses */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Common Uses</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              What can you use a term loan for?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {uses.map((use) => (
              <div
                key={use.title}
                className="bg-white rounded-2xl p-6 border border-border/50 text-center"
              >
                <div className="p-3 bg-accent/20 rounded-xl inline-flex mb-4 text-primary">
                  {use.icon}
                </div>
                <h3 className="font-semibold mb-2">{use.title}</h3>
                <p className="text-sm text-muted-foreground">{use.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Types */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Options</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Types of business loans
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Different loan types suit different needs. We'll help you choose
              the right option based on your situation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {loanTypes.map((type) => (
              <div
                key={type.title}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <h3 className="font-semibold text-lg mb-2">{type.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {type.description}
                </p>
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Security</p>
                    <p className="text-sm font-medium">{type.security}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Amount</p>
                    <p className="text-sm font-medium">{type.typical}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Term</p>
                    <p className="text-sm font-medium">{type.term}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="section-label mb-4">Requirements</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                What lenders look for
              </h2>
              <p className="text-muted-foreground mb-6">
                Requirements vary by lender and loan type. Traditional banks have
                stricter criteria, while fintech and non-bank lenders are often
                more flexible.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                Check your eligibility
                <FiArrowRight className="size-4" />
              </Link>
            </div>
            <div className="space-y-4">
              {requirements.map((req) => (
                <div
                  key={req.title}
                  className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl"
                >
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <FiCheck className="size-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{req.title}</h3>
                    <p className="text-sm text-muted-foreground">{req.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">
              The Process
            </p>
            <h2 className="font-serif text-3xl md:text-4xl">
              How we help you get funded
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((item) => (
              <div key={item.step} className="bg-white/10 rounded-2xl p-6">
                <div className="w-10 h-10 bg-accent text-accent-foreground rounded-full flex items-center justify-center font-serif text-lg mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-primary-foreground/80">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-primary-foreground/90 mb-4">
              Timeline varies by lender - from 24 hours for some fintech lenders
              to 2-4 weeks for traditional banks.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Need business funding?"
        title="Let's find the right loan for your business"
        description="Book a free consultation and we'll discuss your options."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
